import { useQueries, useQuery } from "@tanstack/react-query";
import { type Key, useEffect, useMemo, useRef, useState } from "react";
import { useListData } from "react-stately";
import { ListBoxItem } from "react-aria-components";
import { SearchableSelectByIdField } from "@/components/form/fields/SearchableSelectByIdField";
import type { DisplayFieldFeedback as DisplayFieldFeedbackType } from "@/components/Atoms/DisplayField/display-field.shared";
import { LabelWithFeedback } from "@/components/base/input/label-with-feedback";
import { MultiSelect } from "@/components/base/select/multi-select";
import type { SelectItemType } from "@/components/base/select/select";
import { getOrgUserAssigneeLabel } from "@/forms/shared/assignee-display-label";
import { useDebounced } from "@/hooks/use-debounced";
import { organizationQueryOptions } from "@/services/organization/organization.options";
import {
	USER_STATUS,
	type OrganizationUserDisplay,
} from "@/services/organization/organization.types";

export const USER_PICKER_SEARCH_DEBOUNCE_MS = 300;

export type UserPickerIdKind = "user" | "organizationUser";

export function userPickerItemId(
	user: { id: string; userId: string },
	idKind: UserPickerIdKind = "user",
): string {
	return idKind === "organizationUser" ? user.id : user.userId;
}

/** Multiple mode stores an id array, or null when nothing is selected. */
export function userFieldValueFromIds(ids: readonly string[]): string[] | null {
	return ids.length === 0 ? null : [...ids];
}

/** Single mode stores a one-element array, or null when cleared. */
export function userFieldValueFromSingleId(id: string | null): string[] | null {
	return id ? [id] : null;
}

type UserPickerBaseProps = {
	organizationId: string;
	/** Which id is stored. Defaults to the platform user id. */
	idKind?: UserPickerIdKind;
	label?: string;
	placeholder?: string;
	searchPlaceholder?: string;
	isDisabled?: boolean;
	isRequired?: boolean;
	isInvalid?: boolean;
	hint?: string;
	feedback?: DisplayFieldFeedbackType;
	onBlur?: () => void;
};

export type UserPickerProps = UserPickerBaseProps &
	(
		| {
				selectionMode: "single";
				value: string | null;
				onChange: (value: string | null) => void;
		  }
		| {
				selectionMode: "multiple";
				value: string[] | null;
				onChange: (value: string[] | null) => void;
		  }
	);

export function UserPicker(props: UserPickerProps) {
	const {
		organizationId,
		idKind = "user",
		label,
		placeholder = "Select users",
		searchPlaceholder = "Search by name",
		isDisabled,
		isRequired,
		isInvalid,
		hint,
		feedback,
		onBlur,
	} = props;
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounced(search, USER_PICKER_SEARCH_DEBOUNCE_MS);
	const searchTerm = debouncedSearch.trim();

	const usersQuery = useQuery({
		...organizationQueryOptions.users.list(organizationId, 1, 25, {
			Status: USER_STATUS.ACTIVE,
			...(searchTerm ? { SearchTerm: searchTerm } : {}),
		}),
		enabled: organizationId.length > 0,
	});

	const items = useMemo(() => {
		const users = usersQuery.data ?? [];
		return users.map((user) => userToSelectItem(user, idKind));
	}, [idKind, usersQuery.data]);

	const selectedIds =
		props.selectionMode === "single"
			? props.value
				? [props.value]
				: []
			: (props.value ?? []);

	const knownLabelsRef = useRef(new Map<string, string>());
	for (const item of items) {
		if (item.label) knownLabelsRef.current.set(item.id, item.label);
	}

	const missingSelectedIds = selectedIds.filter(
		(id) => !knownLabelsRef.current.has(id),
	);
	const selectedUserQueries = useQueries({
		queries:
			idKind === "organizationUser"
				? missingSelectedIds.map((id) =>
						organizationQueryOptions.users.detail(organizationId, id),
					)
				: [],
	});
	for (const query of selectedUserQueries) {
		if (query.data) {
			const item = userToSelectItem(query.data, idKind);
			if (item.label) knownLabelsRef.current.set(item.id, item.label);
		}
	}

	const byId = new Map(items.map((item) => [item.id, item]));
	for (const id of selectedIds) {
		if (!byId.has(id)) {
			byId.set(id, { id, label: knownLabelsRef.current.get(id) ?? id });
		}
	}
	const itemsWithSelection = [...byId.values()];

	const queryText = search.trim().toLowerCase();
	const pickerItems = queryText
		? itemsWithSelection.filter(
				(item) =>
					selectedIds.includes(item.id) ||
					(item.label ?? item.id).toLowerCase().includes(queryText),
			)
		: itemsWithSelection;

	if (props.selectionMode === "multiple") {
		return (
			<UserPickerMultiple
				items={pickerItems}
				value={props.value ?? []}
				label={label}
				placeholder={placeholder}
				isDisabled={isDisabled || organizationId.length === 0}
				isRequired={isRequired}
				isInvalid={isInvalid}
				hint={hint}
				feedback={feedback}
				inputValue={search}
				onInputChange={setSearch}
				onChange={props.onChange}
				onBlur={onBlur}
			/>
		);
	}

	return (
		<SearchableSelectByIdField
			field={{
				state: { value: props.value, meta: {} },
				handleChange: props.onChange,
				handleBlur: () => {
					onBlur?.();
				},
			}}
			items={itemsWithSelection}
			label={label ?? ""}
			placeholder={placeholder}
			searchPlaceholder={searchPlaceholder}
			isDisabled={isDisabled || organizationId.length === 0}
		>
			{(item) => (
				<ListBoxItem id={item.id} textValue={item.label ?? item.id}>
					{item.label}
				</ListBoxItem>
			)}
		</SearchableSelectByIdField>
	);
}

function userToSelectItem(
	user: OrganizationUserDisplay,
	idKind: UserPickerIdKind,
): SelectItemType {
	return {
		id: userPickerItemId(user, idKind),
		label: getOrgUserAssigneeLabel(user),
	};
}

function UserPickerMultiple({
	items,
	value,
	label,
	placeholder,
	isDisabled,
	isRequired,
	isInvalid,
	hint,
	feedback,
	inputValue,
	onInputChange,
	onChange,
	onBlur,
}: {
	items: SelectItemType[];
	value: string[];
	label?: string;
	placeholder: string;
	isDisabled?: boolean;
	isRequired?: boolean;
	isInvalid?: boolean;
	hint?: string;
	feedback?: DisplayFieldFeedbackType;
	inputValue: string;
	onInputChange: (value: string) => void;
	onChange: (value: string[] | null) => void;
	onBlur?: () => void;
}) {
	const selectedItems = useListData<SelectItemType>({ initialItems: [] });

	useEffect(() => {
		const desired = items.filter((item) => value.includes(item.id));
		const signature = (list: SelectItemType[]) =>
			list.map((item) => `${item.id}\u0001${item.label ?? ""}`).join("\0");
		if (signature(selectedItems.items) === signature(desired)) return;
		for (const item of [...selectedItems.items]) {
			selectedItems.remove(item.id);
		}
		for (const item of desired) {
			selectedItems.append(item);
		}
	}, [items, selectedItems, value]);

	return (
		<div className="flex flex-col gap-1.5">
			<LabelWithFeedback
				label={label}
				isRequired={isRequired}
				isFilled={value.length > 0}
				feedback={feedback}
			/>
			<MultiSelect
				items={items}
				selectedItems={selectedItems}
				aria-label={label}
				placeholder={placeholder}
				placeholderIcon={null}
				showTagAvatar={false}
				isDisabled={isDisabled}
				isRequired={isRequired}
				isInvalid={isInvalid}
				hint={hint}
				inputValue={inputValue}
				onInputChange={onInputChange}
				onItemInserted={(key: Key) => {
					const next = String(key);
					onInputChange("");
					if (value.includes(next)) return;
					onChange(userFieldValueFromIds([...value, next]));
				}}
				onItemCleared={(key: Key) => {
					onChange(
						userFieldValueFromIds(
							value.filter((entry) => entry !== String(key)),
						),
					);
				}}
				onBlur={onBlur}
			>
				{(item) => (
					<MultiSelect.Item id={item.id}>{item.label}</MultiSelect.Item>
				)}
			</MultiSelect>
		</div>
	);
}
